// Finance math utilities. Pure functions, no side effects.

export const toNum = (v, fallback = 0) => {
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : fallback;
};

// Monthly payment for an amortized loan
export function monthlyPayment(principal, annualRatePct, years) {
    const r = annualRatePct / 100 / 12;
    const n = years * 12;
    if (!principal || !n) return 0;
    if (r === 0) return principal / n;
    return (principal * r) / (1 - Math.pow(1 + r, -n));
}

// Build amortization schedule — summarised by year to keep arrays small
export function amortizationByYear(principal, annualRatePct, years, extraMonthly = 0) {
    const r = annualRatePct / 100 / 12;
    const n = years * 12;
    const basePayment = monthlyPayment(principal, annualRatePct, years);
    const payment = basePayment + extraMonthly;
    let balance = principal;
    const schedule = [];
    let year = 0;
    let yearPrincipal = 0;
    let yearInterest = 0;
    for (let m = 1; m <= n * 2 && balance > 0.01; m++) {
        const interest = balance * r;
        let principalPaid = payment - interest;
        if (principalPaid > balance) principalPaid = balance;
        balance -= principalPaid;
        yearPrincipal += principalPaid;
        yearInterest += interest;
        if (m % 12 === 0 || balance <= 0.01) {
            year++;
            schedule.push({
                year,
                principal: yearPrincipal,
                interest: yearInterest,
                balance: Math.max(0, balance),
            });
            yearPrincipal = 0;
            yearInterest = 0;
            if (balance <= 0.01) break;
        }
    }
    return schedule;
}

// Compound interest future value with optional monthly contributions
export function futureValueCompound(principal, monthly, annualRatePct, years, compoundsPerYear = 12) {
    const n = compoundsPerYear;
    const r = annualRatePct / 100;
    const t = years;
    const fvPrincipal = principal * Math.pow(1 + r / n, n * t);
    // Future value of annuity (monthly contributions), compounded monthly approximation
    const rm = r / 12;
    const months = t * 12;
    const fvMonthly =
        rm === 0 ? monthly * months : monthly * ((Math.pow(1 + rm, months) - 1) / rm);
    return fvPrincipal + fvMonthly;
}

// Year-by-year growth for chart
export function compoundGrowthByYear(principal, monthly, annualRatePct, years) {
    const out = [];
    const rm = annualRatePct / 100 / 12;
    let balance = principal;
    let totalContrib = principal;
    for (let y = 1; y <= years; y++) {
        for (let m = 0; m < 12; m++) {
            balance = balance * (1 + rm) + monthly;
            totalContrib += monthly;
        }
        out.push({
            year: y,
            balance: Math.round(balance),
            contributions: Math.round(totalContrib),
            interest: Math.round(balance - totalContrib),
        });
    }
    return out;
}

// Debt payoff using avalanche (highest APR first) or snowball (lowest balance first)
export function payoffDebts(debts, extraMonthly, strategy = "avalanche") {
    // debts: [{name, balance, apr, minPayment}]
    const list = debts.map((d) => ({ ...d }));
    let month = 0;
    let totalInterest = 0;
    const timeline = [];
    const maxMonths = 480;
    while (list.some((d) => d.balance > 0.01) && month < maxMonths) {
        month++;
        // interest accrual
        list.forEach((d) => {
            if (d.balance > 0) {
                const interest = (d.balance * (d.apr / 100)) / 12;
                d.balance += interest;
                totalInterest += interest;
            }
        });
        // pay minimums
        let availableExtra = extraMonthly;
        list.forEach((d) => {
            if (d.balance > 0) {
                const pay = Math.min(d.minPayment, d.balance);
                d.balance -= pay;
            }
        });
        // sort target order
        const sorted = list
            .filter((d) => d.balance > 0)
            .sort((a, b) => (strategy === "snowball" ? a.balance - b.balance : b.apr - a.apr));
        for (const d of sorted) {
            if (availableExtra <= 0) break;
            const pay = Math.min(availableExtra, d.balance);
            d.balance -= pay;
            availableExtra -= pay;
        }
        timeline.push({
            month,
            balance: list.reduce((s, d) => s + Math.max(0, d.balance), 0),
        });
    }
    return { months: month, totalInterest, timeline };
}

// Credit card payoff (single balance)
export function payoffCard(balance, apr, monthlyPayment, extra = 0) {
    const payment = monthlyPayment + extra;
    const rm = apr / 100 / 12;
    let bal = balance;
    let months = 0;
    let interest = 0;
    const series = [{ month: 0, balance: bal }];
    const maxMonths = 600;
    while (bal > 0.01 && months < maxMonths) {
        const ir = bal * rm;
        if (payment <= ir) return { months: Infinity, totalInterest: Infinity, series };
        bal += ir;
        const pay = Math.min(payment, bal);
        bal -= pay;
        interest += ir;
        months++;
        if (months % 3 === 0 || bal <= 0.01) series.push({ month: months, balance: Math.max(0, bal) });
    }
    return { months, totalInterest: interest, series };
}

// Savings goal: required monthly contribution to reach target
export function savingsMonthlyRequired(target, current, annualRatePct, years) {
    const rm = annualRatePct / 100 / 12;
    const n = years * 12;
    const fvCurrent = current * Math.pow(1 + rm, n);
    const needed = target - fvCurrent;
    if (needed <= 0) return 0;
    if (rm === 0) return needed / n;
    return needed * (rm / (Math.pow(1 + rm, n) - 1));
}

// Simple US federal income tax (2024 single brackets, illustrative only)
const FED_BRACKETS_SINGLE = [
    [11600, 0.1],
    [47150, 0.12],
    [100525, 0.22],
    [191950, 0.24],
    [243725, 0.32],
    [609350, 0.35],
    [Infinity, 0.37],
];
const FED_BRACKETS_MARRIED = [
    [23200, 0.1],
    [94300, 0.12],
    [201050, 0.22],
    [383900, 0.24],
    [487450, 0.32],
    [731200, 0.35],
    [Infinity, 0.37],
];
const STD_DEDUCTION = { single: 14600, married: 29200 };

export function federalIncomeTax(gross, filing = "single") {
    const brackets = filing === "married" ? FED_BRACKETS_MARRIED : FED_BRACKETS_SINGLE;
    const taxable = Math.max(0, gross - STD_DEDUCTION[filing]);
    let tax = 0;
    let prev = 0;
    for (const [cap, rate] of brackets) {
        if (taxable > cap) {
            tax += (cap - prev) * rate;
            prev = cap;
        } else {
            tax += (taxable - prev) * rate;
            break;
        }
    }
    return { tax: Math.max(0, tax), taxable, deduction: STD_DEDUCTION[filing] };
}

// Rent vs Buy: returns total net cost over N years for each
export function rentVsBuy({
    homePrice,
    downPaymentPct,
    mortgageRate,
    mortgageYears,
    propertyTaxRate,
    maintenanceRate,
    homeAppreciation,
    monthlyRent,
    rentIncrease,
    investReturn,
    years,
}) {
    const down = (homePrice * downPaymentPct) / 100;
    const loan = homePrice - down;
    const mPmt = monthlyPayment(loan, mortgageRate, mortgageYears);
    let homeValue = homePrice;
    let loanBalance = loan;
    let totalMortgagePaid = 0;
    let totalOwnerCosts = 0;
    const rm = mortgageRate / 100 / 12;

    let rent = monthlyRent;
    let totalRent = 0;
    let investPortfolio = down; // alternative: invest the down payment
    const invM = investReturn / 100 / 12;

    for (let y = 1; y <= years; y++) {
        for (let m = 0; m < 12; m++) {
            if (loanBalance > 0) {
                const interest = loanBalance * rm;
                const principal = Math.min(mPmt - interest, loanBalance);
                loanBalance -= principal;
                totalMortgagePaid += mPmt;
            }
            const monthlyTax = (homeValue * (propertyTaxRate / 100)) / 12;
            const monthlyMaint = (homeValue * (maintenanceRate / 100)) / 12;
            totalOwnerCosts += monthlyTax + monthlyMaint;
            totalRent += rent;
            // invest the monthly difference between owning and renting (if owning is more)
            const ownMonthly = (loanBalance > 0 ? mPmt : 0) + monthlyTax + monthlyMaint;
            const diff = ownMonthly - rent;
            if (diff > 0) investPortfolio = investPortfolio * (1 + invM) + diff;
            else investPortfolio = investPortfolio * (1 + invM);
        }
        homeValue *= 1 + homeAppreciation / 100;
        rent *= 1 + rentIncrease / 100;
    }
    const buyNet =
        down + totalMortgagePaid + totalOwnerCosts - (homeValue - loanBalance); // negative = net gain
    const rentNet = totalRent - (investPortfolio - down);
    return {
        buyNet,
        rentNet,
        homeValue,
        investPortfolio,
        loanBalance,
        totalMortgagePaid,
        totalOwnerCosts,
        totalRent,
        winner: buyNet < rentNet ? "buy" : "rent",
    };
}
